import {addOrderRoleKeyFor} from 'app/main/private/welcome/client-portal-landing.helpers';
import {UntypedFormGroup, UntypedFormBuilder} from '@angular/forms';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {BehaviorSubject, from, merge, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, skip, startWith, take, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';

import {FuseConfigService} from '@fuse/services/config.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';
import {LoginService} from 'app/core/login/login.service';
import {ASYNC_TASKS, CLIENT_PORTAL_ORDERS_HOME} from 'app/app.constants';
import {Principal} from 'app/core/auth/principal.service';
import {RoleService} from 'app/blocks/service/api/role.service';
import {CUSTOMER_TRACK_ORDER_FEATURE} from 'app/constants/role-settings/customer';

import {navigation} from 'app/navigation/navigation';
import {NavigationEnd, Router} from '@angular/router';
import {faBell, faRoute} from '@fortawesome/free-solid-svg-icons';
import {ASSISTANT_LABEL} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-label';
import {en_GB, fr_FR, NzI18nService} from 'ng-zorro-antd/i18n';
import {SuperUserService} from 'app/core/auth/super-user.service';
import {NotificationService} from 'app/blocks/service/api/notification.service';
import {SatPopoverComponent} from '@ncstate/sat-popover';
import {getTimeText} from 'app/common/utils/date-utils';
import {DiSearchBarComponent} from 'app/common/di-search-bar/di-search-bar.component';
import {HaulierOrderResource} from 'app/blocks/resource/haulier-order-resource';
import {TrafficSheetResourcesService} from 'app/blocks/service/api/traffic-sheet-resources.service';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';
import {locale as english} from 'app/layout/components/toolbar/i18n/en';
import {locale as french} from 'app/layout/components/toolbar/i18n/fr';
import {Store} from '@ngxs/store';
import {AuthServerProvider} from 'app/core/auth/auth-jwt.service';
import {GetTenantSettings, TrackAsyncRequest} from 'app/blocks/store/actions/common-actions';
import {BulkOperationService} from 'app/blocks/service/bulk-operation.service';
import {ServerEventService} from 'app/blocks/service/server-event.service';
import {CurrencyService} from 'app/blocks/service/api/currency.service';
import {NzModalService} from 'ng-zorro-antd/modal';
import {CheckRouteModalComponent} from 'app/main/private/common/check-route-modal/check-route-modal.component';
import {RunService} from 'app/blocks/service/api/run.service';
import {DialogService} from 'app/common/dialog/dialog-service';
// version.json sits outside src (no path alias reaches it)
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import versionJson from '../../../../../version.json';
import {LoggedInService} from 'app/blocks/service/logged-in.service';
import {SettingService} from 'app/blocks/service/api/settings.service';
import {saveAs} from 'file-saver';
import {ToastrService} from 'ngx-toastr';
import {PublicTrackingService} from 'app/blocks/service/api/public-tracking.service';

const FILTER_ON_DATES_PREFERENCE = 'filterOnDatesPreference';

@Component({
    // Angular 22 defaults to OnPush; Eager preserves the previous behaviour.
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'toolbar',
    templateUrl: './toolbar-ant.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ToolbarComponent implements OnInit, OnDestroy {
    /** Angular 22 can drop the zone tick that renders a value assigned from a promise. */
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _roleService = inject(RoleService);

    /** Customer (incl. sub-contractor) logins only get Track Order, and only when their role turns it on (Roles > Features). */
    readonly customerTrackOrderEnabled$: Observable<boolean> = this._roleService.roleSettings.pipe(
        map((settings) => settings?.[CUSTOMER_TRACK_ORDER_FEATURE] === true)
    );

    // Added by RouteMagic
    faBell = faBell;
    readonly askLabel = ASSISTANT_LABEL;
    faRoute = faRoute;

    // End
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];
    name = '';
    tenant = '';
    appVersionNo = '';

    /**
     * Phone only (<600px): the desktop action cluster does not fit a 56px bar, so it slides up as a
     * bottom sheet instead. Always false on desktop - nothing there can toggle it.
     */
    mobileActionsOpen = false;

    quickSearchForm: UntypedFormGroup;
    radioValuesSearchForm = ['Run', 'Order', 'Driver', 'Vehicle', 'Trailer'];
    public searchTermIsEmpty = true; // controls nzSuffix changes for search input

    /**
     * Role-setting key that decides whether "Add Order" shows. Customer logins always use the Daily Manifest
     * control (Roles > Daily Manifest > Control) on every page; others use it on that page and the haulier-orders one elsewhere.
     */
    addOrderRoleKey$: Observable<string>;

    timerSubscription;
    notifications$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    bulkProgress$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    @ViewChild('notifications', {static: false}) notifications: SatPopoverComponent;
    @ViewChild('orderPopover', {static: false}) orderPopover: SatPopoverComponent;

    @ViewChild(DiSearchBarComponent, {static: true})
    public readonly searchbar: DiSearchBarComponent;
    @Input() showSearchBar = true;

    // Private
    private _unsubscribeAll: Subject<void>;
    isSuperuser = false;
    isUserCustomer = false;

    /**
     * Toolbar actions are for staff only: hidden for super admins and customers (sub-contractors are customers too).
     * Stays false until the identity resolves, so a customer never sees them flash in.
     */
    get showStaffActions(): boolean {
        return !this.isSuperuser && this.isUserCustomer === false;
    }

    is_dark_mode = false;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        public _store: Store,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private _principal: Principal,
        private _loginService: LoginService,
        private _router: Router,
        private _NzI18nService: NzI18nService,
        private _notificationService: NotificationService,
        private _runService: RunService,
        private superUserService: SuperUserService,
        private _fb: UntypedFormBuilder,
        private tsService: TrafficSheetResourcesService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _authServerProvider: AuthServerProvider,
        private _serverEventService: ServerEventService,
        public _loggedInServie: LoggedInService,
        private currencySerice: CurrencyService,
        private nzModalService: NzModalService,
        private _dialogService: DialogService,
        private orderResource: HaulierOrderResource,
        private settingService: SettingService,
        private _bulkOperationService: BulkOperationService,
        private _toastr: ToastrService,
        private _publicTrackingService: PublicTrackingService
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, french);
        // Set the defaults
        this.quickSearchForm = this._fb.group({
            searchType: [{value: 'ORDER', disabled: false}],
            searchQuery: '',
            filterOnDates: [false]
        });
        this.userStatusOptions = [
            {
                title: 'Online',
                icon: 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon: 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon: 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id: 'en',
                title: 'English',
                flag: 'uk'
            },
            {
                id: 'fr',
                title: 'French',
                flag: 'fr'
            }
        ];

        this.appVersionNo = versionJson.appVersion;

        this.isSuperuser = this.superUserService.getIsSuperUser();
        this.isUserCustomer = this._principal.isUserCustomer();
        if (this.isUserCustomer == undefined) {
            this._principal.identity().then(
                (userIdentity) => {
                    this.isUserCustomer = this._principal.isUserCustomer();
                    this._cdr.markForCheck();
                },
                (rejectionReason) => {
                    this.isUserCustomer = undefined;
                }
            );
        }
        this.navigation = navigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject<void>();

        // Re-evaluated when the identity resolves too: a customer must never get the haulier-orders key before it is known.
        this.addOrderRoleKey$ = merge(
            this._router.events.pipe(filter((event) => event instanceof NavigationEnd)),
            from(this._principal.identity()).pipe(catchError(() => of(null)))
        ).pipe(
            startWith(null),
            map(() => addOrderRoleKeyFor(this._router.url, !!this._principal.isUserCustomer())),
            distinctUntilChanged()
        );

        this.settingService.getTenantSetting('defaultSearchType').then((setting) => {
            if (setting && !this.isUserCustomer) {
                this.quickSearchForm.patchValue({
                    searchType: setting
                });
            }
        });

        // 'filterOnDates' is a one-shot signal the list pages reset after reading, so the user's own
        // choice is remembered under a separate key. The tenant default only applies until they pick one.
        const filterOnDatesPreference = window.localStorage.getItem(FILTER_ON_DATES_PREFERENCE);
        if (filterOnDatesPreference != null) {
            this.quickSearchForm.patchValue({filterOnDates: filterOnDatesPreference === 'true'}, {emitEvent: false});
        } else {
            this.settingService.getTenantSetting('defaultFilterOnDates').then((setting) => {
                if (setting && !this.isUserCustomer && window.localStorage.getItem(FILTER_ON_DATES_PREFERENCE) == null) {
                    this.quickSearchForm.patchValue({filterOnDates: setting}, {emitEvent: false});
                }
            });
        }
        this.quickSearchForm.controls.filterOnDates.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            window.localStorage.setItem(FILTER_ON_DATES_PREFERENCE, String(!!value));
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.syncThemeState();
        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings) => {
            this.horizontalNavbar = settings.layout.navbar.position === 'top';
            this.rightNavbar = settings.layout.navbar.position === 'right';
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
        });

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, {
            id: this._translateService.currentLang
        });

        this._store
            .select((state) => state.common[ASYNC_TASKS])
            .subscribe((asyncTasks: any) => {
                if (asyncTasks) {
                    this.bulkProgress$.next(asyncTasks);
                }
            });

        // Subscribe to bulk operations for immediate visibility
        this._bulkOperationService.getActiveBulkOperations().subscribe((bulkOperations) => {
            if (bulkOperations && bulkOperations.length > 0) {
                // Convert bulk operations to the format expected by the UI
                const bulkProgressData = bulkOperations.map((op) => ({
                    id: op.id,
                    operationName: op.operationName,
                    successCount: op.successCount || 0,
                    failureCount: op.failureCount || 0,
                    totalCount: op.totalCount || op.runIds.length,
                    inProgress: op.status === 'in_progress'
                }));
                this.bulkProgress$.next(bulkProgressData);
            }
        });

        this._principal.getAuthenticationState().subscribe((userIdentity) => {
            if (userIdentity) {
                this.name = userIdentity.firstName + ' ' + userIdentity.lastName;
                this.tenant = userIdentity.tenantId;
                if (!this.isSuperuser) {
                    this._store.dispatch(new TrackAsyncRequest());
                    this._store.dispatch(new GetTenantSettings());
                    this.loadNotifications();
                }
            }
        });

        if (!this.isSuperuser) {
            this._serverEventService.manifestGeneratedEvent.pipe(debounceTime(1000)).subscribe((data) => {
                if (this._principal.isAuthenticated() && data.data && !document.hidden && document.hasFocus()) {
                    const bytes = Uint8Array.from(atob(data.data), (c) => c.charCodeAt(0));
                    saveAs(new Blob([bytes], {type: 'application/pdf'}), 'bulk-manifest.pdf');
                    this._serverEventService.clearFirebaseEvent('manifest-generated');
                }
            });
            let lastProcessedExportTimestamp: string = null;
            this._serverEventService.orderExportCompletedEvent.pipe(debounceTime(1000)).subscribe((data) => {
                if (
                    this._principal.isAuthenticated() &&
                    data.data &&
                    data.timestamp !== lastProcessedExportTimestamp &&
                    !document.hidden &&
                    document.hasFocus()
                ) {
                    lastProcessedExportTimestamp = data.timestamp;
                    this._serverEventService.orderExportCompletedEvent.next({});
                    this._serverEventService.clearFirebaseEvent('order-export-completed');
                    const bytes = Uint8Array.from(atob(data.data), (c) => c.charCodeAt(0));
                    saveAs(new Blob([bytes], {type: 'text/csv'}), `order-export-${new Date().toISOString().slice(0, 10)}.csv`);
                    this._toastr.success('Order export completed!');
                }
            });
            this._serverEventService.userNotificationEvent.pipe(debounceTime(1000)).subscribe((data) => {
                if (this._principal.isAuthenticated()) {
                    this.loadNotifications();
                }
            });
            this._serverEventService.tenantSettingUpdateEvent.pipe(debounceTime(1000)).subscribe((data) => {
                if (this._principal.isAuthenticated()) {
                    this._store.dispatch(new GetTenantSettings());
                    this.currencySerice.getTenantCurrency();
                }
            });
            this._serverEventService.exhangeRateUpdateEvent.pipe(debounceTime(1000)).subscribe((data) => {
                if (this._principal.isAuthenticated()) {
                    this.currencySerice.getCurrencyMap();
                }
            });
            this._serverEventService.reindexEvent
                .pipe(skip(2))
                .pipe(debounceTime(1000))
                .subscribe((data) => {
                    if (data?.data?.message) {
                        this._dialogService.showMessage({
                            message: data?.data?.message,
                            confirmText: 'common.ok'
                        });
                    }
                });
        }
    }

    async onSearch(): Promise<void> {
        window.localStorage.setItem('quickSearchParams', String(this.quickSearchForm.value.searchQuery));
        window.localStorage.setItem('quickSearchParamsState', 'notUsed');
        window.localStorage.setItem('filterOnDates', String(this.quickSearchForm.value.filterOnDates));
        if (this.quickSearchForm.value.searchType == 'ORDER') {
            // Portal users can't open the all-orders list (clientPortalOrdersRedirectGuard bounces them to
            // the manifest), so send them straight to the manifest, which reads the term back.
            window.location.href = this._principal.isUserCustomer() ? CLIENT_PORTAL_ORDERS_HOME : 'orders/haulier-orders';
        } else {
            const runId = parseInt(this.quickSearchForm.value.searchQuery);
            if (!isNaN(runId)) {
                const run = await this._runService.getRunWithExceptionCatch(runId);
                if (run) {
                    this.tsService.openRunView(runId, 'NEW_WINDOW');
                }
            }
            window.location.href = 'runs/runs';
        }
    }

    clearSearch(): void {
        this.quickSearchForm.controls.searchQuery.setValue('');
    }

    onSearchValueChange(): void {
        if (this.quickSearchForm.controls.searchQuery.value != '') {
            this.searchTermIsEmpty = false;
        } else {
            this.searchTermIsEmpty = true;
        }
    }

    loadNotifications(): void {
        if (this._authServerProvider.getToken()) {
            this._notificationService.getNotifications().then((notifications) => {
                this.notifications$.next(notifications.body);
            });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void {
        // Do your search here...
        console.log(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
        if (lang.id !== 'en') {
            this._NzI18nService.setLocale(fr_FR);
        } else {
            this._NzI18nService.setLocale(en_GB);
        }
    }

    markNotificationsAsRead(): void {
        this._notificationService.markNotificationsAsRead().then((result) => {
            this.loadNotifications();
        });
    }

    getDateTimeAsString(dateStr: string): string {
        return getTimeText(dateStr);
    }
    openCheckRouteModal() {
        this.nzModalService.create({
            nzContent: CheckRouteModalComponent,
            nzData: {},
            nzWidth: 1040,
            nzStyle: {top: '20px'},
            nzClosable: true,
            nzFooter: null
        });
    }

    addNewOrderWindowed() {
        window.open('orders/haulier-orders/new?hideSideBar=true', '_blank', 'location=yes,height=1080,width=1500,scrollbars=yes,status=yes').focus();
    }

    addNewOrder() {
        this.orderResource.navigate.toNew();
    }

    trackOrder() {
        window.open('order-tracking?hideSideBar=true', '_blank', 'location=yes,height=1080,width=1500,scrollbars=yes,status=yes').focus();
    }

    @HostListener('window:themeChanged')
    onThemeChanged(): void {
        this.syncThemeState();
    }

    private syncThemeState(): void {
        this.is_dark_mode = localStorage.getItem('isDarkModeActive') === 'true';
    }

    goHome(): void {
        this._router.navigate(['/']);
    }
}
